import axios from 'axios';

export function resolveComparisonApiBase(): string {
  const raw = import.meta.env.VITE_API_URL;
  if (typeof raw === 'string' && raw.trim().length > 0) {
    return raw;
  }

  return '/api';
}

const API_BASE = resolveComparisonApiBase();

export class ComparisonDeletionError extends Error {
  status: number;
  code?: string;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'ComparisonDeletionError';
    this.status = status;
    this.code = code;
  }
}

export interface ComparisonRequest {
  title: string;
  description?: string;
  basePrompt: string;
  selectedModels: SelectedModel[];
  comparisonCriteria?: ComparisonCriteria;
  metadata?: ComparisonMetadata;
  isPublic?: boolean;
}

export interface SelectedModel {
  providerId: string;
  modelId: string;
  configuration?: ModelConfiguration;
}

export interface ModelConfiguration {
  temperature?: number;
  maxTokens?: number;
  topP?: number;
  frequencyPenalty?: number;
  presencePenalty?: number;
  stop?: string[];
}

export interface ComparisonCriteria {
  accuracy: number;
  clarity: number;
  creativity: number;
  relevance: number;
  completeness: number;
  coherence: number;
  efficiency: number;
  helpfulness: number;
  originality: number;
  engagement: number;
}

export interface ComparisonMetadata {
  tags?: string[];
  category?: string;
  language?: string;
  expectedResponseLength?: 'short' | 'medium' | 'long';
  domain?: string;
  useCase?: string;
}

export type ComparisonStatus = 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';

export interface Comparison {
  id: string;
  userId: string;
  title: string;
  description?: string;
  prompt: string;
  status: ComparisonStatus;
  totalModels: number;
  completedModels: number;
  totalCost: number;
  createdAt: string;
  updatedAt: string;
}

export interface ModelResponse {
  id: string;
  comparisonId: string;
  modelId: string;
  providerId: string;
  providerName?: string | null;
  modelName?: string | null;
  response: string;
  content?: string;
  status: 'pending' | 'completed' | 'failed';
  tokenUsage?: {
    inputTokens: number;
    outputTokens: number;
    totalTokens: number;
  };
  cost: number;
  responseTime?: number;
  latencyMs?: number | null;
  errorMessage?: string;
  rawResponse?: unknown;
  scoreOverall?: number | null;
  createdAt: string;
  updatedAt: string;
  model?: {
    id: string;
    providerId: string;
    name?: string | null;
    displayName?: string | null;
  } | null;
  provider?: {
    id: string;
    name?: string | null;
  } | null;
}

export interface ScoringResult {
  id: string;
  responseId: string;
  criterionName: string;
  score: number;
  reasoning: string;
  strengths?: string[];
  weaknesses?: string[];
  suggestions?: string[];
  weightedScore: number;
  overallAssessment?: string;
  winningFactors?: string[];
  improvementAreas?: string[];
  judgeModel: string;
  judgeProvider: string;
  judgingCost: number;
  createdAt: string;
}

export interface ComparisonAnalysis {
  id: string;
  comparisonId: string;
  narrativeExplanation: string;
  keyDifferentiators: string[];
  modelAgreements: Record<string, any>;
  modelDisagreements: Record<string, any>;
  recommendedWinnerId?: string;
  recommendedWinnerReasoning?: string;
  recommendedWinnerConfidence?: number;
  judgeModel: string;
  judgeProvider: string;
  totalJudgingCost: number;
  createdAt: string;
}

export interface ComparisonResults {
  comparison: Comparison;
  comparisons?: Comparison[]; // For legacy components
  responses: ModelResponse[];
  scoringResults: ScoringResult[];
  analysis?: ComparisonAnalysis;
  modelDetails: Array<{
    id: string;
    name: string;
    displayName: string;
    providerId: string;
    providerName: string;
  }>;
  winner_ai_id?: string | null;
  winner_score?: number | null;
}

export interface CreateComparisonResponse {
  comparisonId: string;
  status: ComparisonStatus;
  estimatedDuration: number;
  estimatedCost: number;
  message: string;
}

export interface ComparisonProgress {
  comparisonId: string;
  status: ComparisonStatus;
  completedModels: number;
  totalModels: number;
  currentModel?: string;
  errors: Array<{
    modelId: string;
    error: string;
    timestamp: string;
  }>;
  estimatedTimeRemaining?: number;
}

export interface ExportOptions {
  format: 'json' | 'csv' | 'pdf';
  includeResponses: boolean;
  includeScoring: boolean;
  includeAnalysis: boolean;
  includeMetadata: boolean;
}

class ComparisonService {
  private getAuthHeaders() {
    const token = localStorage.getItem('authToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  /**
   * Create a new comparison
   *
   * @throws {QuotaExceededError} If user has exceeded their comparison quota
   * @throws {Error} For other API errors
   */
  async createComparison(request: ComparisonRequest): Promise<CreateComparisonResponse> {
    console.log('[ANON-FLOW] createComparison service called', {
      hasPrompt: !!request.basePrompt,
      promptLength: request.basePrompt?.length || 0,
      promptPreview: request.basePrompt?.substring(0, 50) + '...',
      modelCount: request.selectedModels?.length || 0,
      models: request.selectedModels?.map(m => m.modelId),
      hasAuthToken: !!localStorage.getItem('authToken'),
      endpoint: `${API_BASE}/comparison/create`,
      withCredentials: true
    });

    try {
      const response = await axios.post(`${API_BASE}/comparison/create`, request, {
        headers: this.getAuthHeaders(),
        withCredentials: true, // Include cookies for anonymous quota tracking
      });

      console.log('[ANON-FLOW] createComparison response received', {
        comparisonId: response.data.comparisonId,
        status: response.data.status,
        message: response.data.message
      });

      return response.data;
    } catch (error: any) {
      console.error('[ANON-FLOW] createComparison error', {
        status: error?.response?.status,
        statusText: error?.response?.statusText,
        errorCode: error?.response?.data?.error,
        errorMessage: error?.response?.data?.message,
        hasQuotaDetails: error?.response?.status === 403 && error?.response?.data?.error === 'QUOTA_EXCEEDED'
      });

      // Check if this is a quota exceeded error (403 with QUOTA_EXCEEDED)
      if (error?.response?.status === 403 && error?.response?.data?.error === 'QUOTA_EXCEEDED') {
        console.warn('[ANON-FLOW] Quota exceeded detected', error.response.data);
        // Re-throw with quota error details attached
        const quotaError = new Error(error.response.data.message || 'Comparison quota exceeded');
        (quotaError as any).quotaDetails = error.response.data;
        (quotaError as any).upgradeUrl = error.response.data.upgradeUrl;
        throw quotaError;
      }

      throw error;
    }
  }

  /**
   * Get comparison results
   * @deprecated Demo data removed. Use API endpoints directly:
   * - GET /api/comparisons for list
   * - GET /api/comparisons/:id/responses for responses
   * Use ResultsNewRoot component with useComparisons/useResponses hooks.
   */
  async getComparisonResults(comparisonId: string): Promise<ComparisonResults> {
    throw new Error(
      `getComparisonResults() no longer provides demo data.\n` +
      `Use API endpoints:\n` +
      `- GET /api/comparisons for list\n` +
      `- GET /api/comparisons/:id/responses for responses\n` +
      `Use ResultsNewRoot component (RESULTS_NEW_DESIGN flag) instead.\n` +
      `(Attempted: ${comparisonId})`
    );
  }

  /**
   * Get user's comparisons
   */
  async getUserComparisons(
    limit = 20,
    offset = 0,
    status?: ComparisonStatus
  ): Promise<{ comparisons: Comparison[]; total: number }> {
    try {
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: offset.toString()
      });
      if (status) params.append('status', status);

      const response = await axios.get(`${API_BASE}/comparison/list?${params}`, {
        headers: this.getAuthHeaders(),
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching user comparisons:', error);
      throw error;
    }
  }

  /**
   * Get comparison progress
   */
  async getComparisonProgress(comparisonId: string): Promise<ComparisonProgress> {
    try {
      const response = await axios.get(`${API_BASE}/comparison/${comparisonId}/progress`, {
        headers: this.getAuthHeaders(),
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching comparison progress:', error);
      throw error;
    }
  }

  /**
   * Cancel a running comparison
   */
  async cancelComparison(comparisonId: string): Promise<void> {
    try {
      await axios.post(`${API_BASE}/comparison/${comparisonId}/cancel`, {}, {
        headers: this.getAuthHeaders(),
      });
    } catch (error) {
      console.error('Error canceling comparison:', error);
      throw error;
    }
  }

  /**
   * Delete a comparison
   * Future-proof: Sends Authorization header for when Firebase auth is added
   */
  async deleteComparison(comparisonId: string): Promise<void> {
    const correlationId = `comparison_delete_${comparisonId}_${Date.now()}`;

    try {
      await axios.post(
        `${API_BASE}/comparison/delete`,
        { comparisonId },
        {
          headers: {
            'Content-Type': 'application/json',
            ...this.getAuthHeaders(), // Future: Will include Firebase JWT token
            'X-Correlation-Id': correlationId,
          },
        }
      );
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const response = error.response;
        const status = response?.status ?? 0;
        const data = (response?.data ?? {}) as {
          error?: string;
          message?: string;
          code?: string;
        };
        const message =
          data.message ||
          data.error ||
          error.message ||
          'Failed to delete comparison';

        throw new ComparisonDeletionError(message, status, data.code);
      }

      throw new ComparisonDeletionError('Failed to delete comparison', 0);
    }
  }

  /**
   * Export comparison results
   */
  async exportComparison(
    comparisonId: string,
    options: ExportOptions
  ): Promise<Blob> {
    try {
      const response = await axios.post(
        `${API_BASE}/comparison/${comparisonId}/export`,
        options,
        {
          headers: {
            ...this.getAuthHeaders(),
          },
          responseType: 'blob'
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error exporting comparison:', error);
      throw error;
    }
  }

  /**
   * Share a comparison publicly
   */
  async shareComparison(
    comparisonId: string,
    isPublic: boolean
  ): Promise<{ shareUrl?: string; message: string }> {
    try {
      const response = await axios.post(
        `${API_BASE}/comparison/${comparisonId}/share`,
        { isPublic },
        {
          headers: this.getAuthHeaders(),
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error sharing comparison:', error);
      throw error;
    }
  }

  /**
   * Get public comparison (no auth required)
   */
  async getPublicComparison(comparisonId: string): Promise<ComparisonResults> {
    try {
      const response = await axios.get(`${API_BASE}/comparison/public/${comparisonId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching public comparison:', error);
      throw error;
    }
  }
}

// Favorites management (localStorage-based)
export class FavoritesService {
  private STORAGE_KEY = 'smartprompt_favorites';

  getFavorites(): string[] {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  }

  isFavorite(comparisonId: string): boolean {
    return this.getFavorites().includes(comparisonId);
  }

  toggleFavorite(comparisonId: string, currentState: boolean): void {
    const favorites = this.getFavorites();
    if (currentState) {
      // Remove from favorites
      const updated = favorites.filter(id => id !== comparisonId);
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(updated));
    } else {
      // Add to favorites
      if (!favorites.includes(comparisonId)) {
        favorites.push(comparisonId);
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(favorites));
      }
    }
  }
}

export const comparisonService = new ComparisonService();
export const favoritesService = new FavoritesService();
